import { Component, ElementRef, NgZone, TemplateRef, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { AuthService } from "../../core/auth.service";
import { Router } from "@angular/router";
import { TokenService } from "../../core/token.service";
import { ToastService } from "@app/services/toast.service";
import { NgxSpinnerService } from "ngx-spinner";
import { EncryptionService } from "../services/encryption.service";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { FirebaseService } from "../services/firebase.service";
import { environment } from "../../environments/environment";
import { KeyService } from "../services/key.service";
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';

type MfaStep = 'login' | 'register-mfa' | 'enter-totp';

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: "app-login",
    imports: [NgClass, FormsModule],
    templateUrl: "./login.component.html",
    styleUrls: ["./login.component.scss"],})
export class LoginComponent {
  @ViewChild('forceLoginPopup') forceLoginPopup!: TemplateRef<any>;
  @ViewChild('registerDevicePopup') registerDevicePopup!: TemplateRef<any>;
  @ViewChild('helpPopup') helpPopup!: TemplateRef<any>;

  /**
   * The TOTP <input #totpInputRef> exists in both the 'register-mfa' and
   * 'enter-totp' steps. The setter runs the moment Angular renders it, so we can
   * focus it without setTimeout.
   */
  private _totpInput?: ElementRef<HTMLInputElement>;
  @ViewChild('totpInputRef')
  set totpInputRef(el: ElementRef<HTMLInputElement> | undefined) {
    this._totpInput = el;
    // auto-focus only on the 'enter-totp' screen (and never while a modal is open)
    if (el && this.currentStep === 'enter-totp' && !this.modalservice.hasOpenModals()) {
      el.nativeElement.focus();
    }
  }
  environment: any = environment;

  // ── Login Fields ──────────────────────────────────────────
  username = "";
  password = "";
  error: string = '';
  userType: any = "Dealer";
  showPassword: boolean = false;

  // ── CAPTCHA ───────────────────────────────────────────────
  captchaText: string = '';
  captchaInput: string = '';
  captchaError: boolean = false;

  // ── MFA ───────────────────────────────────────────────────
  currentStep: MfaStep = 'login';
  totpSingleCode: string = '';
  qrCodeUrl: string = '';
  secretKey: string = '';
  showSecretKey: boolean = false;
  mfaError: string = '';
  isLoading: boolean = false;
  closeResult: any = "";
  force_login: boolean = false;
  is_mfa_activated: boolean = true;
  isLocal: boolean = false;

  // ── Help popup ────────────────────────────────────────────
  helpTab: 'first' | 'returning' = 'first';

  // ── Internal state ────────────────────────────────────────
  /**
   * Tracks whether the current user still needs to register MFA (false)
   * or has MFA already set up (true). Used after a 429 to route
   * back to the correct MFA screen.
   */
  private _mfaEnabled: boolean = false;

  /**
   * Stores the secret returned with the QR code so we can re-send it
   * along with the TOTP when the user verifies after scanning.
   */
  private _pendingSecret: string = '';

  constructor(
    private authService: AuthService,
    private router: Router,
    private tokenService: TokenService,
    private ngZone: NgZone,
    private toastr: ToastService,
    private spinner: NgxSpinnerService,
    public encryptionService: EncryptionService,
    private modalservice: NgbModal,
    private fs: FirebaseService,
    private keyService: KeyService
  ) { }

  ngOnInit(): void {
    this.spinner.hide('main');
    if (this.tokenService.isLoggedIn()) {
      this.redirectBasedOnUserType();
    }
    this.keyService.getToken();
    this.generateCaptcha();
    const hostname = window.location.hostname;
    // this.isLocal=hostname === "localhost" || hostname === "vas-dev.cv.tatamotors.com" || hostname === "vas-qa.cv.tatamotors.com";
  }

  sanitizeUsername(event: Event): void {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    this.username = input.value.replace(/[^a-zA-Z0-9._-]/g, '');
    input.value = this.username;
  }

  sanitizeTotpCode(event: Event): void {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    this.totpSingleCode = input.value.replace(/[^0-9]/g, '');
    input.value = this.totpSingleCode;
  }

  // ── CAPTCHA ───────────────────────────────────────────────
  generateCaptcha(): void {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    this.captchaText = Array.from({ length: 6 }, () =>
      chars[Math.floor(Math.random() * chars.length)]
    ).join('');
    this.captchaInput = '';
    // this.captchaError = false;
  }
  hideCaptchaError(): void {
    this.captchaError = false;
  }

  isCaptchaValid(): boolean {
    return this.captchaInput.trim() === this.captchaText;
  }

  // ── Redirect ──────────────────────────────────────────────
  private redirectBasedOnUserType(): void {
    const userType = this.encryptionService.getFromLocalStorage('user_type');
    const normalizedUserType = userType ? userType.toLocaleLowerCase() : null;
    this.router.navigate([this.getRedirectUrl(normalizedUserType)]);
  }

  private getRedirectUrl(userType: string | null): string {
    switch (userType) {
      case 'dealer': return 'dealer/pipeline/A0';
      case 'admin': return 'admin/dashboard';
      case 'gdc': return 'tml/pipeline/A0';
      case 'call_centre': return 'callcenter/pipeline/A0';
      case 'approver': return 'tml/price-deviation/pending';
      default: return '404';
    }
  }

  // ══════════════════════════════════════════════════════════
  // STEP 1 — LOGIN
  //   Payload: { username, password, totp: "" }
  //   On success:
  //     mfa_enabled = false  → QR setup screen  (+ qr_code & secret in response)
  //     mfa_enabled = true   → TOTP entry screen
  //   On 429: open force-login modal, retain mfa_enabled to route correctly
  // ══════════════════════════════════════════════════════════
  login(): void {
    if (!this.isCaptchaValid() && this.force_login == false) {
      this.captchaError = true;
      this.generateCaptcha();
      return;
    }

    this.spinner.show('main');
    this.mfaError = '';

    const payload = {
      username: this.username,
      password: this.encryptionService.encryptString(this.password),
      app_name: 'com.tatamotors.vas.crm',
      totp: '',
      force_login: this.force_login
    };

    this.authService.login(payload).subscribe({
      next: (response) => {
        this.spinner.hide('main');
        const data = response;

        if ("mfa_enabled" in data) {

          this._mfaEnabled = data?.mfa_enabled ?? false;
          this.force_login = data?.force_login || false;
          if (this.force_login) {
            this.openForceLoginModal();
          }

          if (!this._mfaEnabled) {
            // MFA not registered yet — show QR setup screen
            this.qrCodeUrl = data?.mfa_data?.qr_image || '';
            this.secretKey = data?.mfa_data?.secret || '';
            this._pendingSecret = this.secretKey;
            this.currentStep = 'register-mfa';
          } else {
            // MFA already set up — go straight to TOTP entry
            this.currentStep = 'enter-totp';
          }
        } else {
          this.is_mfa_activated = false
          // If MFA is not required, proceed with normal login success flow
          this.handleLoginSuccess(response);
        }
      },
      error: (err) => {
        this.spinner.hide('main');
        const msg = err?.error?.msg || 'Something went wrong. Please try again.';
        this.toastr.error(msg);
        this.generateCaptcha();

        if (err.status === 429) {
          this.is_mfa_activated = err?.error?.is_mfa_activated
          this.force_login = true;
          this.openForceLoginModal();
        } else {
          this.error = 'Login failed';
          this.fs.addFirebaseNewEvent('Login Failed', {
            page_title: 'Login',
            user_name: this.username,
            res: err?.error?.msg,
          });
        }
      },
    });
  }

  // ── Force Login Modal ─────────────────────────────────────
  private openForceLoginModal(): void {
    const modalRef = this.modalservice.open(this.forceLoginPopup, { centered: true, size: 'sm' });
    // modal is fully removed -> hand focus back to the TOTP box (if that step is showing)
    modalRef.hidden.subscribe(() => this.focusTotp());
    modalRef.result.then(
        (result: any) => {
          this.closeResult = `Closed with: ${result}`;
          if (result === 'Force Login') {
            this.force_login = true;
            if (!this.is_mfa_activated) {
              this.login();
            }
          }
        },
        () => {
          this.closeResult = 'Dismissed';
          this.currentStep = 'login'; // handle cancel here
          this.generateCaptcha()
        }
      );
  }


  // forceLogin(): void {
  //   this.login();
  // }

  // ══════════════════════════════════════════════════════════
  // STEP 2a — VERIFY TOTP AFTER QR SCAN (register-mfa screen)
  //   Payload: { username, password, totp, secret }
  // ══════════════════════════════════════════════════════════

  // ══════════════════════════════════════════════════════════
  // STEP 2b — VERIFY TOTP (enter-totp screen)
  //   Payload: { username, password, totp }
  // ══════════════════════════════════════════════════════════
  submitTotp(): void {
    if (this.totpSingleCode.length !== 6) {
      this.mfaError = 'Please enter the complete 6-digit code.';
      return;
    }

    this.isLoading = true;
    this.mfaError = '';

    const payload: any = {
      username: this.username,
      password: this.encryptionService.encryptString(this.password),
      totp: Number(this.totpSingleCode),
      force_login: this.force_login,
      app_name: this.userType == 'Dealer' ? this.environment.CRM_APP_NAME : this.environment.GDC_APP_NAME

    };
    this.spinner.show('main');

    // If the user just scanned the QR, also send the secret
    if (this.currentStep === 'register-mfa' && this._pendingSecret) {
      payload['secret'] = this._pendingSecret;
    }

    this.authService.login(payload).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.spinner.hide('main');

        this.handleLoginSuccess(response);
      },
      error: (err) => {
        this.isLoading = false;
        this.spinner.hide('main');

        this.mfaError = err?.error?.msg || 'Invalid code. Please try again.';
        this.totpSingleCode = '';
        this.focusTotp();
        if (err.status === 429) {
          this.force_login = true;
          // Preserve whatever mfa_enabled state the server told us (may be in the error body)
          this.openForceLoginModal();
        } else {
          this.error = 'Login failed';
          this.fs.addFirebaseNewEvent('Login Failed', {
            page_title: 'Login',
            user_name: this.username,
            res: err?.error?.msg,
          });
        }
      },
    });
  }

  // ── Handle successful final login ─────────────────────────
  private handleLoginSuccess(response: any): void {
    const data = response.data;

    // // inside handleLoginSuccess
    let encryptedConfig = data.config;

    localStorage.setItem('fconfig', encryptedConfig);
    this.fs.initFirebase();
        // "tml_payment_active": true,
        // "amc_memi_active": true,
        // "is_fms_active": true,
        // "is_ew_active": true,
        // "fms_memi_active": true

    this.encryptionService.setToLocalStorage('position_type', data.user_info.position_type.toUpperCase());
    this.encryptionService.setToLocalStorage('position_name', data.user_info.position_name);
    this.encryptionService.setToLocalStorage('user_type', data.user_info.type.toLowerCase());
    this.encryptionService.setToLocalStorage('username', this.username);
    let tml_payment_active: boolean = response?.dealer_ac?.tml_payment_active ?? false;
    this.encryptionService.setToLocalStorage('tml_payment_active', tml_payment_active.toString());
    let amc_memi_active: boolean = response?.dealer_ac?.amc_memi_active ?? false;
    this.encryptionService.setToLocalStorage('amc_memi_active', amc_memi_active.toString());
    const dealerAc = response?.dealer_ac || {};
    this.encryptionService.setToLocalStorage('is_fms_active',(dealerAc.is_fms_active ?? false).toString());
    this.encryptionService.setToLocalStorage('is_ew_active',(dealerAc.is_ew_active ?? false).toString());
    this.encryptionService.setToLocalStorage('fms_memi_active',(dealerAc.fms_memi_active ?? false).toString());
    this.encryptionService.setToLocalStorage('fms_qemi_active',(dealerAc.fms_qemi_active ?? false).toString());
    this.encryptionService.setToLocalStorage('amc_qemi_active',(dealerAc.amc_qemi_active ?? false).toString());
    localStorage.setItem('isLogin', 'true');

    this.tokenService.saveToken(data.access_token);
    this.tokenService.saveRefreshToken(data.refresh_token);

    const userType = data.user_info.type.toLowerCase();
    const route = this.getRedirectUrl(userType);

    // Redirect only AFTER the Firebase event has finished (success, error or late response),
    // so a slow addFirebaseNewEvent can no longer block or cancel the navigation. No setTimeout.
    const logEvent: any = this.fs.addFirebaseNewEvent('Login Success MFA', {
      page_title: 'Login',
      user_name: this.username,
      res: 'Login Success',
    });
    this.afterFirebaseEvent(logEvent, () => this.finishLogin(route));
  }

  /**
   * Runs `done` exactly once when the Firebase call completes.
   * Works whether addFirebaseNewEvent returns an Observable, a Promise or nothing.
   */
  private afterFirebaseEvent(result: any, done: () => void): void {
    let fired = false;
    const once = () => {
      if (!fired) {
        fired = true;
        done();
      }
    };
    if (result && typeof result.subscribe === 'function') {
      // Observable: complete -> redirect; error -> still redirect
      result.subscribe({ error: () => once(), complete: () => once() });
    } else if (result && typeof result.then === 'function') {
      // Promise: settle (resolve or reject) -> redirect
      result.then(() => once(), () => once());
    } else {
      once();
    }
  }

  private finishLogin(route: string): void {
    this.ngZone.run(() => {
      this.router.navigate([route]).then(() => {
        this.toastr.success('Successfully Logged In', 'Success');
      });
    });
  }

  // ══════════════════════════════════════════════════════════
  // REGISTER NEW DEVICE
  //   Opens a confirmation modal.
  //   Payload: { username, password, register_new_device: true }
  //   On success: mfa_enabled = false  → go to QR setup screen
  // ══════════════════════════════════════════════════════════
  openRegisterDeviceModal(): void {
    this.modalservice
      .open(this.registerDevicePopup, { centered: true, size: 'sm' })
      .result.then(
        (result: any) => {
          if (result === 'Register') {
            this.registerNewDevice();
          }
        },
        () => { }
      );
  }

  registerNewDevice(): void {
    this.isLoading = true;
    this.mfaError = '';

    const payload = {
      username: this.username,
      password: this.encryptionService.encryptString(this.password),
      app_name: 'com.tatamotors.vas.crm',
      force_login: this.force_login,
      register_new_device: true,
    };

    this.authService.login(payload).subscribe({
      next: (response) => {
        this.isLoading = false;
        const data = response;

        // Server should return new QR code & secret for the new device
        this.qrCodeUrl = data?.mfa_data?.qr_image || '';
        this.secretKey = data?.mfa_data?.secret || '';
        this._pendingSecret = this.secretKey;
        this._mfaEnabled = false;
        this.currentStep = 'register-mfa';
        this.toastr.success('Scan the new QR code to register your device.');
      },
      error: (err) => {
        this.isLoading = false;
        this.mfaError = err?.error?.msg || 'Failed to register new device. Please try again.';
        this.toastr.error(this.mfaError);
      },
    });
  }

  // ══════════════════════════════════════════════════════════
  // HELP POPUP — "How to login" guide (CRM login, CAPTCHA, MFA)
  //   tab = 'first'     → first-time user (install app + scan QR)
  //   tab = 'returning' → MFA already set up (just enter the code)
  // ══════════════════════════════════════════════════════════
  openHelpModal(tab: 'first' | 'returning' = 'first'): void {
    this.helpTab = tab;
    const modalRef = this.modalservice.open(this.helpPopup, {
      centered: true,
      size: 'lg',
    });
    // hand focus back to the TOTP box (if that step is showing) once closed
    modalRef.hidden.subscribe(() => this.focusTotp());
    modalRef.result.then(() => { }, () => { });
  }

  // ── MFA Helpers ───────────────────────────────────────────
  toggleSecretKey(): void {
    this.showSecretKey = !this.showSecretKey;
  }

  copySecret(): void {
    navigator.clipboard.writeText(this.secretKey);
    this.toastr.success('Secret key copied!');
  }

  /** Focus the TOTP input if it is currently rendered. */
  private focusTotp(): void {
    this._totpInput?.nativeElement.focus();
  }

  /** Enter key inside the TOTP input behaves like clicking Verify. */
  onTotpEnter(event: Event): void {
    event.preventDefault();
    if (this.isLoading || this.totpSingleCode.length !== 6) {
      return;
    }
    this.submitTotp();
  }

  goBackToLogin(): void {
    this.currentStep = 'login';
    this.mfaError = '';
    this.totpSingleCode = '';
    this._pendingSecret = '';
    this.generateCaptcha();
  }


  // ── Misc ──────────────────────────────────────────────────
  clearForm(): void {
    this.username = '';
    this.password = '';
  }

  logout(): void {
    this.authService.logout();
    this.clearForm();
  }
}